import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { AutocompleteRenderOptionState } from "@mui/material/Autocomplete";

import { Autocomplete, AutocompleteMulti } from "src/components/Autocomplete";
import Button from "src/components/Button";
import CheckboxAutocomplete from "src/components/CheckboxAutocomplete";
import { createSelectableObject, SelectableObject } from "src/components/Form/types";
import type { Course, Operation, Position, Role } from "src/features/relations-tree/searchUtils";

interface AircraftDropdownItem {
  id: number;
  kind: "designator" | "type";
  name: string;
  designatorId?: number | null;
}

interface SelectedValues {
  roles?: Role[];
  position?: Position;
  operations?: Operation[];
  aircraft?: AircraftDropdownItem[];
}

interface FormValues {
  roles?: Role[];
  courses?: Course[];
}

interface AddRelationsFormProps {
  aircraftList: AircraftDropdownItem[];
  onChange: {
    roles: (roles: Role[]) => void;
    position: (position: Position | undefined) => void;
    operations: (operations: Operation[]) => void;
    aircraft: (aircraft: AircraftDropdownItem[]) => void;
    courses: (courses: Course[]) => void;
    clear: () => void;
    submit: () => void;
  };
  courses: Course[];
  error: string | null;
  values: FormValues | null;
  selectedValues: SelectedValues | null;
}

const buildAircraftOptions = (aircraftList: AircraftDropdownItem[]): AircraftDropdownItem[] => {
  if (!aircraftList?.length) return [];
  const designators = aircraftList.filter((aircraft) => aircraft.kind === "designator");
  const types = aircraftList.filter((aircraft) => aircraft.kind === "type");
  const result: AircraftDropdownItem[] = [];
  for (const designator of designators) {
    result.push(designator);
    result.push(...types.filter((type) => type.designatorId === designator.id));
  }
  return result;
};

const renderAircraftOption = (
  props: React.HTMLAttributes<HTMLLIElement>,
  option: AircraftDropdownItem,
  _state: AutocompleteRenderOptionState,
) => (
  <li
    {...props}
    key={`${option.kind}-${option.id}`}
    style={{
      ...props.style,
      paddingLeft: option.kind === "type" ? "2rem" : undefined,
      fontWeight: option.kind === "designator" ? 600 : undefined,
    }}
  >
    {`${option.name} (${option.id})`}
  </li>
);

const aircraftOptionEquals = (option: AircraftDropdownItem, value: AircraftDropdownItem) =>
  option.kind === value.kind && option.id === value.id;

const filterAircraftOptions = (options: AircraftDropdownItem[], { inputValue }: { inputValue: string }) => {
  if (!inputValue) return options;
  const query = inputValue.toLowerCase();
  const matchingTypeDesignatorIds = new Set(
    options
      .filter((option) => option.kind === "type" && option.name.toLowerCase().includes(query))
      .map((option) => option.designatorId)
      .filter(Boolean),
  );
  const matchingDesignatorIds = new Set(
    options
      .filter((option) => option.kind === "designator" && option.name.toLowerCase().includes(query))
      .map((option) => option.id),
  );
  return options.filter((option) => {
    if (option.kind === "designator")
      return option.name.toLowerCase().includes(query) || matchingTypeDesignatorIds.has(option.id);
    return option.name.toLowerCase().includes(query) || matchingDesignatorIds.has(option.designatorId ?? 0);
  });
};

const AddRelationsForm = ({
  aircraftList,
  onChange,
  courses,
  error,
  values,
  selectedValues,
}: AddRelationsFormProps) => {
  const { t } = useTranslation();
  const multipleRolesSelected = Array.isArray(selectedValues?.roles) && selectedValues.roles.length > 1;

  const orderedAircraftList = useMemo(() => buildAircraftOptions(aircraftList), [aircraftList]);

  const roles = useMemo(
    () => (values?.roles ?? []).map((role) => createSelectableObject(role.id, `${role.name} (${role.id})`)),
    [values?.roles],
  );

  const selectedRoles = useMemo(
    () => (selectedValues?.roles ?? []).map((role) => createSelectableObject(role.id, `${role.name} (${role.id})`)),
    [selectedValues?.roles],
  );

  const handleRolesChange = (roles: SelectableObject[]) => {
    const ids = new Set(roles.map((role) => role.getId()));
    onChange.roles((values?.roles ?? []).filter((role) => ids.has(role.id)));
  };

  const courseItems = useMemo(
    () => (values?.courses ?? []).map((course) => createSelectableObject(course.id, `${course.name} (${course.id})`)),
    [values?.courses],
  );

  const selectedCourses = useMemo(
    () => (courses ?? []).map((course) => createSelectableObject(course.id, `${course.name} (${course.id})`)),
    [courses],
  );

  const operationItems = useMemo(
    () =>
      (selectedValues?.position?.operations ?? []).map((operation) =>
        createSelectableObject(operation.id, `${operation.name} (${operation.id})`),
      ),
    [selectedValues?.position?.operations],
  );

  const selectedOperations = useMemo(
    () =>
      (selectedValues?.operations ?? []).map((operation) =>
        createSelectableObject(operation.id, `${operation.name} (${operation.id})`),
      ),
    [selectedValues?.operations],
  );

  const handleCoursesChange = (courses: SelectableObject[]) => {
    const ids = new Set(courses.map((course) => course.getId()));
    onChange.courses((values?.courses ?? []).filter((course) => ids.has(course.id)));
  };

  const handleOperationsChange = (operations: SelectableObject[]) => {
    const ids = new Set(operations.map((operation) => operation.getId()));
    onChange.operations((selectedValues?.position?.operations ?? []).filter((operation) => ids.has(operation.id)));
  };

  return (
    <Paper variant="outlined" style={{ marginBottom: "1rem", padding: "1rem" }}>
      <Box
        sx={{
          mb: 1,
          display: "flex",
        }}
      >
        <Box
          sx={{
            width: "33%",
            mr: 1,
          }}
        >
          <CheckboxAutocomplete
            label={t("relations_tree.form_select_roles")}
            items={roles}
            selected={selectedRoles}
            onChange={handleRolesChange}
            showSelectAll={false}
          />
        </Box>
        <Box
          sx={{
            width: "33%",
            mr: 1,
          }}
        >
          <Autocomplete
            onChange={(position) => onChange.position(position ?? undefined)}
            value={multipleRolesSelected ? null : (selectedValues?.position ?? null)}
            options={multipleRolesSelected ? [] : (selectedValues?.roles?.[0]?.positions ?? [])}
            label={t("relations_tree.form_select_position")}
            disabled={multipleRolesSelected}
          />
        </Box>
        <Box
          sx={{
            width: "33%",
          }}
        >
          <CheckboxAutocomplete
            label={t("relations_tree.form_select_operation")}
            items={multipleRolesSelected ? [] : operationItems}
            selected={multipleRolesSelected ? [] : selectedOperations}
            onChange={handleOperationsChange}
            showSelectAll={false}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          mb: 1,
          alignItems: "flex-start",
        }}
      >
        <Box
          sx={{
            width: "50%",
            mr: 0.5,
          }}
        >
          <AutocompleteMulti
            onChange={onChange.aircraft}
            value={multipleRolesSelected ? [] : (selectedValues?.aircraft ?? [])}
            options={multipleRolesSelected ? [] : orderedAircraftList}
            label={t("relations_tree.form_select_aircraft")}
            disabled={multipleRolesSelected}
            renderOption={renderAircraftOption}
            isOptionEqualToValue={aircraftOptionEquals}
            getOptionLabel={(item) => (item?.name ? `${item.name} (${item.id})` : "")}
            filterOptions={filterAircraftOptions}
            disableCloseOnSelect
          />
        </Box>
        <Box
          sx={{
            mr: 1,
            mt: 1,
          }}
        >
          <Tooltip title={t("relations_tree.form_select_aircraft_hint")}>
            <InfoOutlinedIcon color="action" fontSize="small" sx={{ cursor: "help" }} />
          </Tooltip>
        </Box>
        <Box
          sx={{
            flex: 1,
          }}
        >
          <CheckboxAutocomplete
            label={t("relations_tree.form_select_courses")}
            items={courseItems}
            selected={selectedCourses}
            onChange={handleCoursesChange}
            showSelectAll={false}
          />
        </Box>
      </Box>
      <Box>
        <Button title={t("relations_tree.form_clear_title")} onClick={onChange.clear}>
          {t("relations_tree.form_clear")}
        </Button>
        <Button title={t("relations_tree.form_add_title")} onClick={onChange.submit}>
          {t("relations_tree.form_add")}
        </Button>
        {error && <Typography color="error">{error}</Typography>}
      </Box>
    </Paper>
  );
};

export default AddRelationsForm;
